import {
  LOGIN_BLOCKED_ERROR_CODE,
  PENDING_AUTH_TOAST_KEY,
  STORE_READ_ONLY_ERROR_CODE,
} from "@/@types/Errors";
import { toast } from "@/hooks/use-toast";
import i18n from "@/translations";
import axios from "axios";

const getCookieValue = (name: string) => {
  if (typeof document === "undefined") {
    return null;
  }

  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split("=").slice(1).join("="));
};

const getStoredAuthToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return getCookieValue("mundpayToken") || localStorage.getItem("@token");
};

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL as string,
  headers: {
    "Access-Control-Allow-Origin": "*",
  },
});

export const setAuthToken = (token: string | null) => {
  if (token) {
    const normalizedToken = token.replace(/"/g, "");
    api.defaults.headers.common["Authorization"] = `Bearer ${normalizedToken}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

api.interceptors.request.use(
  (config) => {
    const token = getStoredAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token.replace(/"/g, "")}`;
    }

    const locale = getCookieValue("mundpay-locale") || i18n.language || "pt";
    config.headers["X-Lang"] = locale;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const clearSellerSession = () => {
  localStorage.removeItem("@token");
  document.cookie = "mundpayToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = "mundpay_login_by_imp=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { t } = i18n;
    const status = error.response?.status;
    const errorCode = error.response?.data?.error_code;
    const onLoginPage = window.location.pathname === "/login";

    if (status === 403 && errorCode === STORE_READ_ONLY_ERROR_CODE) {
      toast({
        title: t("store_read_only.title", { ns: "errors" }),
        description: t("store_read_only.description", { ns: "errors" }),
        variant: "error",
      });
      return Promise.reject(error);
    }

    if (status === 401 && errorCode === LOGIN_BLOCKED_ERROR_CODE && !onLoginPage) {
      sessionStorage.setItem(PENDING_AUTH_TOAST_KEY, LOGIN_BLOCKED_ERROR_CODE);
      clearSellerSession();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (status === 401 && !onLoginPage) {
      clearSellerSession();
      window.location.href = "/login";
      toast({
        title: t("errors:401.title"),
        description: t("errors:401.description"),
        variant: "destructive",
      });
    }
    if (status === 500) {
      toast({
        title: t("errors:500.title"),
        description: t(error.response?.data?.message || "errors:500.description"),
        variant: "destructive",
      });
    }
    return Promise.reject(error);
  }
);


export const api_nexus = axios.create({
  baseURL: import.meta.env.VITE_NEXUS_API_URL as string,
  headers: {
    "Access-Control-Allow-Origin": "*",
  },
});
